import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Plane, Plus, Edit, Trash2, RefreshCw, Globe } from 'lucide-react';

const AdminAirports = () => {
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadAirports();
  }, []);

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const loadAirports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('jwt') || localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/origin-airports', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAirports(data.origin_airports || []);
      } else {
        showMessage('Failed to load airports', 'error');
      }
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const deleteAirport = async (code) => {
    if (!window.confirm(`Delete airport "${code}"?`)) return;

    try {
      const token = localStorage.getItem('jwt') || localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/origin-airports/${code}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        showMessage('Airport deleted successfully', 'success');
        loadAirports();
      } else {
        const data = await response.json();
        showMessage(data.detail || 'Failed to delete airport', 'error');
      }
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    }
  };

  // Group airports by region
  const airportsByRegion = airports.reduce((acc, airport) => {
    const region = airport.region || 'Other';
    if (!acc[region]) acc[region] = [];
    acc[region].push(airport);
    return acc;
  }, {});

  const regionColors = {
    'Asia Pacific': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    'Europe': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    'North America': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
    'South America': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
    'Middle East': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    'Africa': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
    'Other': 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Origin Airports</h1>
          <p className="text-muted-foreground">
            Manage origin airports for demand forecasting
          </p>
        </div>
        
        <div className="flex space-x-2">
          <Button onClick={loadAirports} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Airport
          </Button>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-4 rounded-md ${
          messageType === 'error' ? 'bg-destructive/10 text-destructive' :
          messageType === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
          'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
        }`}>
          {message}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Plane className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">{airports.length}</p>
                <p className="text-sm text-muted-foreground">Total Airports</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">{Object.keys(airportsByRegion).length}</p>
                <p className="text-sm text-muted-foreground">Regions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">
                  {new Set(airports.map(a => a.country)).size}
                </p>
                <p className="text-sm text-muted-foreground">Countries</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">
                  {airportsByRegion['Asia Pacific']?.length || 0}
                </p>
                <p className="text-sm text-muted-foreground">Asia Pacific</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Airports by Region */}
      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading airports...</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(airportsByRegion).map(([region, regionAirports]) => (
            <Card key={region}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Globe className="h-5 w-5" />
                    <span>{region}</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${regionColors[region] || regionColors['Other']}`}>
                      {regionAirports.length} airports
                    </span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium">Code</th>
                        <th className="text-left py-2 px-3 font-medium">Name</th>
                        <th className="text-left py-2 px-3 font-medium">City</th>
                        <th className="text-left py-2 px-3 font-medium">Country</th>
                        <th className="text-left py-2 px-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {regionAirports.map((airport) => (
                        <tr key={airport.code} className="border-b hover:bg-muted/50">
                          <td className="py-2 px-3">
                            <code className="text-sm font-bold bg-muted px-2 py-1 rounded">
                              {airport.code}
                            </code>
                          </td>
                          <td className="py-2 px-3 font-medium">{airport.name}</td>
                          <td className="py-2 px-3">{airport.city}</td>
                          <td className="py-2 px-3">{airport.country}</td>
                          <td className="py-2 px-3">
                            <div className="flex space-x-2">
                              <Button variant="outline" size="sm">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => deleteAirport(airport.code)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {airports.length === 0 && !loading && (
        <Card>
          <CardContent className="p-8 text-center">
            <Plane className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No airports found</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminAirports;
